/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { getReferralCode, storeReferralCode } from '../../utils/referral';
import { marketingApi } from '../../services/socialApi';

// A disclosed, in-body conversion CTA for the public SEO surfaces
// (answers / research / news / incidents). The content engine ranks and
// pulls readers in, but the highest-intent detail pages had no path from
// "I found the answer I searched for" to "I can run this myself" — they
// ended on a back-link. This is that path, in the open: it says plainly
// this is Hevolve's own product and links to the real /download route
// (the same CTA AgentLanding and News already use). No fake voice, no
// hidden agent — just the honest next step for someone who came for the
// content and might want the tool.
//
// Fully static (no clock/random/client-only data) so it prerenders
// deterministically and cannot reintroduce the #418/#425 hydration
// mismatches the dynamic surfaces were prone to.
//
// `channel` (optional): a lowercase content-surface tag ('research',
// 'answers', 'news', ...). On click we tag the visitor with it FIRST-TOUCH
// (storeReferralCode never overrides a real inbound ?ref, so an external
// channel that brought them in still wins), and record a click. The download
// on /download and the eventual signup then attribute to that content surface
// in /marketing/growth, so we can see which content actually produces users.
// All client-side, on click only, so nothing changes at render (hydration-safe).
export default function SeoConversionCTA({
  headline = 'Run agents like this on your own machine',
  subhead = 'Hevolve agents are local-first and self-evolving. You build them by talking, and they run on your own hardware — your data never has to leave your device.',
  primaryLabel = 'Get Nunba',
  primaryTo = '/download',
  secondaryLabel = 'Explore the agents',
  secondaryTo = '/agents',
  channel = null,
}) {
  const tag = () => {
    if (!channel) return;
    try {
      const already = getReferralCode();
      storeReferralCode(channel); // first-touch: a real inbound ?ref still wins
      // Only count the channel click when this content surface is the origin
      // (no prior code), so we do not double-attribute an external channel.
      if (!already) marketingApi.track(channel, 'click', 'web').catch(() => {});
    } catch (e) { /* attribution is best-effort, never block the click */ }
  };
  return (
    <Box
      component="section"
      sx={{
        mt: { xs: 5, md: 6 },
        p: { xs: 3, md: 4 },
        borderRadius: 3,
        border: '1px solid rgba(108,99,255,0.28)',
        background:
          'linear-gradient(135deg, rgba(108,99,255,0.14) 0%, rgba(0,210,255,0.06) 100%)',
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: '#8b5cf6', fontWeight: 700, letterSpacing: 1 }}
      >
        Democratic intelligence
      </Typography>
      <Typography
        component="h2"
        variant="h5"
        sx={{ color: '#fff', fontWeight: 800, mt: 0.5, mb: 1.25, lineHeight: 1.25 }}
      >
        {headline}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: 'rgba(255,255,255,0.72)', maxWidth: 640, mb: 3, lineHeight: 1.7 }}
      >
        {subhead}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Button
          component={Link}
          to={primaryTo}
          onClick={tag}
          variant="contained"
          disableElevation
          sx={{
            background: '#6C63FF',
            fontWeight: 700,
            textTransform: 'none',
            px: 3,
            '&:hover': { background: '#5a51e6' },
          }}
        >
          {primaryLabel}
        </Button>
        <Button
          component={Link}
          to={secondaryTo}
          variant="outlined"
          sx={{
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.35)',
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.04)' },
          }}
        >
          {secondaryLabel}
        </Button>
      </Stack>
    </Box>
  );
}
